import { useState } from 'react'
import { Check, Eye, Link as LinkIcon, Send, ThumbsDown } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { Button, cx } from '../ui/kit'
import type { Quote } from '../data/model'
import { oeffentlicheUrl } from '../lib/urls'

/**
 * Das Angebot als Link — und was daraus geworden ist.
 *
 * WARUM ES DAS BRAUCHT: die Annahmeseite war gebaut, aber es gab keinen Weg dorthin.
 * Der Vermieter erzeugte ein Angebot, bekam ein PDF und hatte danach keine
 * Möglichkeit, es online bereitzustellen — obwohl jedes Angebot von Anfang an einen
 * öffentlichen Token trägt und die Seite darauf wartet. Eine fertige Strecke ohne
 * Eingang ist dasselbe wie keine Strecke.
 *
 * WARUM DER STAND DAZUGEHÖRT: „hat er es gelesen?" ist die Frage, wegen der ein
 * Vermieter zwei Tage später anruft. Die Daten liegen längst da — `viewed_at` setzt
 * die öffentliche Seite beim ersten Öffnen, `accepted_at` und `declined_at` beim
 * Entscheiden. Sie standen nur nirgends.
 *
 * WARUM „GESEHEN" NICHT ZÄHLT, WIE OFT: `viewed_at` wird einmal gesetzt und danach
 * nicht mehr angefasst. Ein Zähler wäre eine Verhaltensbeobachtung des Gasts, und die
 * Frage lautet nicht „wie oft", sondern „überhaupt".
 */
export default function QuoteLinkPanel({ quote }: { quote: Quote }) {
  const { lang, formatDate } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const [copied, setCopied] = useState(false)

  // Der Token entsteht in der Datenbank. In der Demo gibt es keinen, und ein Link
  // ins Leere ist schlimmer als kein Link.
  if (!store.live || !quote.public_token) {
    return (
      <p className="no-print text-xs text-muted px-4 pb-3">
        {de
          ? 'Der Online-Link zum Angebot steht im angemeldeten Konto zur Verfügung.'
          : 'The online link for this quote is available in a signed-in account.'}
      </p>
    )
  }

  const url = oeffentlicheUrl(`/q/${quote.public_token}`)

  const decided = quote.accepted_at
    ? { tone: 'ok' as const, icon: Check,
        text: de ? `Angenommen am ${formatDate(quote.accepted_at.slice(0, 10))}`
                 : `Accepted on ${formatDate(quote.accepted_at.slice(0, 10))}` }
    : quote.declined_at
      ? { tone: 'danger' as const, icon: ThumbsDown,
          text: de ? `Abgelehnt am ${formatDate(quote.declined_at.slice(0, 10))}`
                   : `Declined on ${formatDate(quote.declined_at.slice(0, 10))}` }
      : quote.viewed_at
        ? { tone: 'info' as const, icon: Eye,
            text: de ? `Vom Gast geöffnet am ${formatDate(quote.viewed_at.slice(0, 10))}`
                     : `Opened by the guest on ${formatDate(quote.viewed_at.slice(0, 10))}` }
        : { tone: 'neutral' as const, icon: Send,
            text: de ? 'Noch nicht geöffnet' : 'Not opened yet' }

  const Icon = decided.icon

  return (
    <section className="no-print px-4 pb-3 space-y-2">
      <div className="rounded-2xl bg-surface-2 p-3 space-y-2.5">
        <div className="flex items-center gap-2 text-sm">
          <LinkIcon size={15} className="text-muted shrink-0" />
          <span className="font-medium">
            {de ? 'Angebot online' : 'Quote online'}
          </span>
          <span className={cx(
            'ml-auto text-xs flex items-center gap-1.5',
            decided.tone === 'ok' ? 'text-ok'
              : decided.tone === 'danger' ? 'text-danger'
              : decided.tone === 'info' ? 'text-info' : 'text-muted',
          )}>
            <Icon size={13} />
            {decided.text}
          </span>
        </div>

        {quote.decline_reason && (
          <p className="text-sm bg-surface rounded-xl px-3 py-2">
            {de ? 'Begründung des Gasts: ' : 'The guest wrote: '}
            {quote.decline_reason}
          </p>
        )}

        <div className="flex items-center gap-2">
          <input
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 min-w-0 text-xs bg-surface rounded-xl px-3 py-2"
          />
          <Button size="sm" variant="secondary" onClick={() => {
            void navigator.clipboard?.writeText(url)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1500)
          }}>
            {copied ? (de ? 'Kopiert' : 'Copied') : (de ? 'Kopieren' : 'Copy')}
          </Button>
        </div>

        <p className="text-xs text-muted">
          {quote.accepted_at
            ? (de
                ? 'Der Gast hat über diese Seite verbindlich gebucht. Die Buchung steht auf bestätigt.'
                : 'The guest booked through this page. The booking is confirmed.')
            : (de
                ? 'Diesen Link an den Gast schicken. Er sieht dort dasselbe Angebot, kann es verbindlich annehmen und die Anzahlung sofort bezahlen — ohne Konto, auf dem Handy.'
                : 'Send this link to the guest. They see the same quote, can accept it and pay the deposit right away — no account, on their phone.')}
        </p>
      </div>
    </section>
  )
}
